import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { capitalize } from 'es-toolkit';
import { useCallback, useMemo, useState } from 'react';

import { fetchPlanList } from '@/entities/payment/api';
import { CheckoutForm } from '../ui';
import { PlanCard } from '../ui';
import { PlanCardSkeleton } from '../ui';
import { SettingSectionLayout } from '../ui';

import type { Plan } from '@/entities/payment';
import { PLAN_RECURRING_INTERVAL_UNIT } from '@/entities/payment/payment-constants';
import { WORKSPACE_PLAN } from '@/entities/workspace/workspace-constants';
import { Button } from '@/shared/components/shadcn/button';
import { Tabs, TabsList, TabsTrigger } from '@/shared/components/shadcn/tabs';
import { Stage } from '@/shared/components/stage/stage';
import { useWorkspace } from '@/shared/hooks/use-workspace';
import { trackEvent } from '@/shared/track';
import { formatUSDCurrency } from '@/shared/utils/fotmat-currency';
import { talkToSales } from '../helpers/talk-to-sales';

export function PlanPage() {
  const [checkout, setCheckout] = useState<{
    workspaceId: string;
    productId: string;
    priceId: string;
  } | null>(null);

  const { workspace } = useWorkspace();

  const [recurring, setRecurring] = useState<PLAN_RECURRING_INTERVAL_UNIT>(
    PLAN_RECURRING_INTERVAL_UNIT.MONTH,
  );

  const { data, isLoading: plansLoading } = useQuery<Plan[]>({
    queryKey: ['plans'],
    queryFn: fetchPlanList,
  });

  const onPlanSelect = useCallback(
    (plan: Plan) => {
      trackEvent('Plan', 'select_plan', {
        plan: plan.name,
      });
      if (plan.metadata.link) {
        window.open(plan.metadata.link, '_blank');
        return;
      }
      const price = plan.prices.find((price) => price.recurring?.interval === recurring);
      if (!price) {
        return;
      }
      setCheckout({
        workspaceId: workspace?.id,
        productId: plan.id,
        priceId: price.id,
      });
    },
    [recurring, workspace],
  );

  // Plan data (could be extracted to a data file or hook)
  const plans = useMemo(() => {
    return data
      ?.filter((plan) => plan.id.trim() !== '')
      .sort((a, b) => {
        const aOrder = a.metadata?.order ? Number(a.metadata.order) : 0;
        const bOrder = b.metadata?.order ? Number(b.metadata.order) : 0;
        return aOrder - bOrder;
      })
      .map((plan) => {
        const price =
          plan.prices.find((price) => price.recurring?.interval === recurring)?.unit_amount || 0;

        const isCurrentPlan = workspace?.plan.toLocaleLowerCase() === plan.name.toLocaleLowerCase();

        const features =
          plan.features?.map((feature) => ({
            text: feature,
          })) || [];

        const expired = dayjs.unix(workspace?.plan_expired_at).isBefore(dayjs());

        const getButton = (): React.ReactNode | undefined => {
          if (isCurrentPlan) {
            if (!expired) return undefined;
          }
          if (plan.name.toLocaleLowerCase() === 'trial') return undefined;
          if (workspace?.plan === WORKSPACE_PLAN.BASIC) {
            if (plan.name.toLocaleLowerCase() === 'enterprise')
              return (
                <Button variant="default" className="w-full" onClick={talkToSales}>
                  Talk to sales
                </Button>
              );
          }
          if (workspace?.plan === WORKSPACE_PLAN.ENTERPRISE) {
            if (plan.name.toLocaleLowerCase() === 'basic') return undefined;
          }
          return (
            <Button variant="default" className="w-full" onClick={() => onPlanSelect(plan)}>
              {plan.metadata.button_label}
            </Button>
          );
        };

        const getPrice = (): string => {
          const label = plan.metadata.label;
          if (label) {
            return label;
          }
          switch (recurring) {
            case 'day':
              return formatUSDCurrency(price * 30);
            case 'month':
              return formatUSDCurrency(price);
            case 'year':
              return formatUSDCurrency(price / 12);
            default:
              throw new Error('Invalid recurring interval');
          }
        };

        const getPriceSuffix = () => {
          if (plan.metadata.label) {
            return undefined;
          }
          return '/ month';
        };

        return {
          title: capitalize(plan.name),
          description: plan.desc,
          price: getPrice(),
          priceSuffix: getPriceSuffix(),
          features: features,
          button: getButton(),
          highlight: true,
          badge: isCurrentPlan ? (expired ? 'Expired' : 'Current plan') : undefined,
        };
      });
  }, [data, onPlanSelect, recurring, workspace]);

  if (checkout) {
    return (
      <CheckoutForm
        workspaceId={checkout.workspaceId}
        productId={checkout.productId}
        priceId={checkout.priceId}
      />
    );
  }

  return (
    <SettingSectionLayout className="max-w-5xl items-center gap-10 px-4 py-10">
      <div className="flex w-full max-w-xl flex-col items-center gap-8">
        <h1 className="text-primary text-center font-sans text-4xl leading-10 font-bold">
          Choose the plan that grows with your social insights
        </h1>
      </div>
      <Tabs
        value={recurring}
        onValueChange={(v) => setRecurring(v as PLAN_RECURRING_INTERVAL_UNIT)}
        className="flex w-full flex-col items-center"
      >
        <TabsList className="bg-secondary h-10 rounded-md">
          <TabsTrigger
            value="month"
            className="data-[state=active]:bg-background data-[state=active]:text-primary h-full rounded-sm px-3 py-1.5 text-sm font-medium"
          >
            Monthly
          </TabsTrigger>
          <TabsTrigger
            value="year"
            className="data-[state=active]:bg-background data-[state=active]:text-primary h-full rounded-sm px-3 py-1.5 text-sm font-medium"
          >
            Annually
          </TabsTrigger>
        </TabsList>
      </Tabs>
      <Stage className="flex w-full gap-6">
        <Stage.Scene status={plansLoading}>
          <PlanCardSkeleton className="flex-1" />
          <PlanCardSkeleton className="flex-1" />
          <PlanCardSkeleton className="flex-1" />
        </Stage.Scene>
        <Stage.Scene data={plans}>
          {(plans) => (
            <>
              {plans.map((plan) => (
                <PlanCard key={plan.title} {...plan} />
              ))}
            </>
          )}
        </Stage.Scene>
      </Stage>
    </SettingSectionLayout>
  );
}
